import * as React from 'react';
import { createPortal } from 'react-dom';
import { noop } from '@deltasierra/utilities-object';
import { TypedExtract } from '@deltasierra/shared';
import { BootstrapSize } from '../theme';

export type ModalProps = {
    children: React.ReactNode;
    onClose?: () => void;
    show: boolean;
    onShow?: () => void;
    closeOnBackdropClick?: boolean;
    staticHeight?: number;
    staticWidth?: number;
    size?: TypedExtract<BootstrapSize, 'lg' | 'sm'>;
};

/**
 * This is the base modal component. All other modals should extend from this.
 *
 * @example
 * <Modal show={this.state.showModal} onClose={() => this.setState({ showModal: false })} >
 *     <ModalHeader>This is a modal!</ModalHeader>
 *     <ModalBody></ModalBody>
 *     <ModalFooter>
 *         <Button onClick={() => this.setState({ showModal: false })}>Close Modal</Button>
 *     </ModalFooter>
 * </Modal>
 * @param props
 * @returns React.ReactElement
 * @see ModalHeader
 * @see ModalBody
 * @see ModalFooter
 */
export const Modal: React.FunctionComponent<ModalProps> = React.memo(
    ({
        children,
        closeOnBackdropClick = true,
        onClose = noop,
        onShow = noop,
        show,
        size,
        staticHeight,
        staticWidth,
    }) => {
        const [shown, setShown] = React.useState(show);
        const [className, setClassName] = React.useState<'' | 'in'>('');

        React.useEffect(() => {
            let timeoutId: any;
            if (show !== shown) {
                // The 'any' here is to get around an issue with tcat and the return value of setTimeout
                if (show) {
                    setShown(show);
                    onShow();
                } else {
                    setClassName('');
                    timeoutId = setTimeout(() => setShown(show), 300);
                }
            }
            return () => clearTimeout(timeoutId);
        }, [onShow, show, shown]);

        React.useEffect(() => {
            let timeoutId: any;
            if (shown) {
                /*
                 * Timeout the adding of the 'in' class, this is required because of how the below component works
                 * If you don't use the delay, the component will display instantly without the transition animation
                 */
                timeoutId = setTimeout(() => setClassName('in'), 100);
            }
            document.body.classList.toggle('modal-open', shown);
            return () => clearTimeout(timeoutId);
        }, [shown]);

        return createPortal(
            <>
                {shown && (
                    <>
                        <div
                            className={`modal-backdrop fade ${className}`}
                            style={{ display: 'block' }}
                            onClick={onClose}
                        />
                        <div
                            className={`modal fade ${className}`}
                            style={{ display: 'block' }}
                            onClick={closeOnBackdropClick ? onClose : undefined}
                        >
                            <div
                                className={`modal-dialog ${size ? `modal-${size}` : ''}`}
                                style={{ height: staticHeight, width: staticWidth }}
                                onClick={event => event.stopPropagation()}
                            >
                                <div className="modal-content" style={{ height: staticHeight, width: staticWidth }}>
                                    {children}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </>,
            document.body,
        );
    },
);
Modal.displayName = 'Modal';

export type ModalHeaderProps = {
    title?: string;
    children?: React.ReactNode;
};

/**
 * Header for a modal. You can specify either a title or children props.
 *
 * @example
 * // Simple usage with a title
 * <ModalHeader title={{ keyId='COMMON.MODAL.TITLE' }}></ModalHeader>
 * @example
 * // Using custom content
 * <ModalHeader>
 *     <img src="/my-cool-image.png" alt="Modal title" />
 * </ModalHeader>
 * @param props - Modal header props
 * @param props.title - The modal title
 * @param props.children - React children
 * @returns React.ReactElement
 */
export const ModalHeader: React.FunctionComponent<ModalHeaderProps> = ({ children, title }) => (
    <div className="modal-header branded-alternate">
        {title && (
            <h3 className="modal-title">
                <p>{title}</p>
            </h3>
        )}
        {!title && children}
    </div>
);
ModalHeader.displayName = 'ModalHeader';

export const ModalBody: React.FunctionComponent<
    React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }
> = ({ children, ...props }) => (
    <div className="modal-body" {...props}>
        {children}
    </div>
);
ModalBody.displayName = 'ModalBody';

export const ModalFooter: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => (
    <div className="modal-footer">{children}</div>
);
ModalFooter.displayName = 'ModalFooter';

export type ModalWithHeader = ModalProps & {
    title: string;
};

/**
 * Simplified modal component with a title.
 *
 * @example
 * <ModalWithHeader title={{ keyId: 'COMMON.MY_MODAL_TITLE' }}>
 *     // Insert modal contents...
 * </ModalWithHeader>
 * @param props - Props
 * @returns React.ReactElement
 */
export const ModalWithHeader: React.FunctionComponent<ModalWithHeader> = React.memo(
    ({ children, closeOnBackdropClick = true, onClose, onShow, show, title }) => (
        <Modal closeOnBackdropClick={closeOnBackdropClick} show={show} onClose={onClose} onShow={onShow}>
            <ModalHeader title={title} />
            {children}
        </Modal>
    ),
);
ModalWithHeader.displayName = 'BaseModal';
